import React from "/vendor/react";
import PropTypes from "prop-types";
import gql from "/vendor/graphql-tag";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  withStyles,
} from "/vendor/@material-ui/core";

import {
  useApolloClient,
  useBreakpoint,
  useQuery,
} from "/lib/component/util";
import { Loader } from "/lib/component/base";
import { useManageResourceStatusToast } from "/lib/component/toast";

import putWrapped from "/lib/mutation/putWrapped";

const HANDLER_TYPES = [
  ["pipe", "Pipe (run a command)"],
  ["set", "Set (fan out to other handlers)"],
  ["tcp", "TCP (send to a socket)"],
  ["udp", "UDP (send to a socket)"],
];

const isSocketType = (type) => type === "tcp" || type === "udp";

// Split a textarea/text value into a clean list of strings. Accepts either
// newline or comma separated values.
const parseList = (value) =>
  (value || "")
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

// Split a textarea into a list preserving each non-empty line (used for
// env_vars which are "KEY=VALUE" strings that may themselves contain commas).
const parseLines = (value) =>
  (value || "")
    .split(/\n/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

// Parse a "key: value" (or "key=value") per line textarea into an object.
const parseKeyVals = (value) => {
  const result = {};
  parseLines(value).forEach((line) => {
    const match = line.match(/^([^:=]+)[:=](.*)$/);
    if (match) {
      result[match[1].trim()] = match[2].trim();
    }
  });
  return result;
};

const keyValsToText = (obj) =>
  Object.keys(obj || {})
    .map((key) => `${key}: ${obj[key]}`)
    .join("\n");

const toInt = (value) => {
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : 0;
};

const emptyValues = (namespace) => ({
  name: "",
  type: "pipe",
  command: "",
  timeout: "",
  filters: "",
  mutator: "",
  handlers: "",
  socketHost: "",
  socketPort: "",
  envVars: "",
  runtimeAssets: "",
  labels: "",
  annotations: "",
  advanced: "",
  __baseSpec: {},
  __baseMeta: { namespace },
});

const valuesFromResource = (json) => {
  // Handler.toJSON returns a wrapped resource:
  //   { type, api_version, metadata: {...}, spec: {...} }
  // so the actual handler fields live under `spec`.
  const meta = (json && json.metadata) || {};
  const baseSpec = { ...((json && json.spec) || {}) };
  delete baseSpec.metadata;

  const list = (arr) => (Array.isArray(arr) ? arr.join("\n") : "");
  const num = (v) => (v ? String(v) : "");
  const socket = baseSpec.socket || {};

  return {
    name: meta.name || "",
    type: baseSpec.type || "pipe",
    command: baseSpec.command || "",
    timeout: num(baseSpec.timeout),
    filters: list(baseSpec.filters),
    mutator: baseSpec.mutator || "",
    handlers: list(baseSpec.handlers),
    socketHost: socket.host || "",
    socketPort: num(socket.port),
    envVars: list(baseSpec.env_vars),
    runtimeAssets: list(baseSpec.runtime_assets),
    labels: keyValsToText(meta.labels),
    annotations: keyValsToText(meta.annotations),
    advanced: "",
    __baseSpec: baseSpec,
    __baseMeta: meta,
  };
};

const buildRaw = (values, namespace) => {
  const spec = { ...values.__baseSpec };

  spec.type = values.type;
  spec.command = values.command;
  spec.timeout = toInt(values.timeout);
  spec.filters = parseList(values.filters);
  spec.mutator = values.mutator.trim();
  spec.handlers = parseList(values.handlers);
  spec.env_vars = parseLines(values.envVars);
  spec.runtime_assets = parseList(values.runtimeAssets);

  if (isSocketType(values.type)) {
    spec.socket = {
      host: values.socketHost.trim(),
      port: toInt(values.socketPort),
    };
  } else {
    delete spec.socket;
  }

  // Advanced overrides win: merge arbitrary spec keys supplied as raw JSON.
  if (values.advanced.trim()) {
    Object.assign(spec, JSON.parse(values.advanced));
  }

  const metadata = { ...values.__baseMeta };
  metadata.name = values.name.trim();
  metadata.namespace = namespace;
  metadata.labels = parseKeyVals(values.labels);
  metadata.annotations = parseKeyVals(values.annotations);

  return JSON.stringify({
    type: "Handler",
    api_version: "core/v2",
    metadata,
    spec,
  });
};

const StyledContentText = withStyles(() => ({
  root: { marginBottom: "1.5rem" },
}))(DialogContentText);

const HandlerConfigForm = ({
  mode,
  namespace,
  initialValues,
  onClose,
  onSuccess,
}) => {
  const client = useApolloClient();
  const createToast = useManageResourceStatusToast();
  const fullScreen = !useBreakpoint("sm", "gt");

  const [values, setValues] = React.useState(initialValues);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);

  const isEdit = mode === "edit";

  const field = (key) => ({
    value: values[key],
    onChange: (ev) => setValues((v) => ({ ...v, [key]: ev.target.value })),
  });

  const validate = () => {
    if (!isEdit && !values.name.trim()) {
      return "Name is required.";
    }
    if (!isEdit && !/^[A-Za-z0-9_.\-]+$/.test(values.name.trim())) {
      return "Name may only contain letters, numbers, and . _ - characters.";
    }
    if (values.type === "pipe" && !values.command.trim()) {
      return "Pipe handlers require a command.";
    }
    if (isSocketType(values.type)) {
      if (!values.socketHost.trim()) {
        return "TCP and UDP handlers require a socket host.";
      }
      if (toInt(values.socketPort) <= 0) {
        return "TCP and UDP handlers require a socket port.";
      }
    }
    if (values.advanced.trim()) {
      try {
        const parsed = JSON.parse(values.advanced);
        if (typeof parsed !== "object" || Array.isArray(parsed)) {
          return "Advanced overrides must be a JSON object.";
        }
      } catch (err) {
        return `Advanced overrides are not valid JSON: ${err.message}`;
      }
    }
    return null;
  };

  const submit = () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError(null);

    const raw = buildRaw(values, namespace);
    // New handlers must not silently overwrite an existing resource.
    const upsert = isEdit;

    putWrapped(client, { raw, upsert })
      .then((result) => {
        if (result && result.errors && result.errors.length > 0) {
          const first = result.errors[0];
          throw new Error(first.message || first.code || "Request failed");
        }

        createToast(Promise.resolve(result), {
          action: isEdit ? "edit" : "create",
          resourceType: "handler",
          resourceName: values.name.trim(),
        });

        onSuccess();
        onClose();
      })
      .catch((err) => {
        setSubmitting(false);
        const networkError = err && err.networkError;
        setError(
          (networkError && networkError.message) ||
            (err && err.message) ||
            "Request failed",
        );
      });
  };

  const close = () => {
    if (!submitting) {
      onClose();
    }
  };

  return (
    <Dialog open fullScreen={fullScreen} maxWidth="md" fullWidth onClose={close}>
      <Loader loading={submitting} passthrough>
        <DialogTitle>
          {isEdit ? `Edit handler ${initialValues.name}` : "New handler"}
        </DialogTitle>
        <DialogContent>
          <StyledContentText>
            A handler decides what happens with an event once a check has run.
            Only the fields relevant to the selected type are required; anything
            not covered by the form below can be set through the raw JSON
            overrides at the bottom.
          </StyledContentText>

          {error && (
            <Typography color="error" paragraph>
              {error}
            </Typography>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Name"
                fullWidth
                required
                disabled={isEdit}
                helperText={
                  isEdit ? "Handler name cannot be changed." : undefined
                }
                {...field("name")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Namespace"
                fullWidth
                disabled
                value={namespace}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select {...field("type")}>
                  {HANDLER_TYPES.map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Determines which fields below are used.
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Timeout (seconds)"
                type="number"
                fullWidth
                helperText="0 disables the timeout."
                {...field("timeout")}
              />
            </Grid>
          </Grid>

          {values.type === "pipe" && (
            <React.Fragment>
              <Divider style={{ margin: "1.5rem 0" }} />
              <Typography variant="subtitle1" gutterBottom>
                Pipe
              </Typography>
              <TextField
                label="Command"
                fullWidth
                required
                multiline
                helperText="Event data is passed to the command over STDIN."
                {...field("command")}
              />
            </React.Fragment>
          )}

          {values.type === "set" && (
            <React.Fragment>
              <Divider style={{ margin: "1.5rem 0" }} />
              <Typography variant="subtitle1" gutterBottom>
                Handler set
              </Typography>
              <TextField
                label="Handlers"
                fullWidth
                multiline
                helperText="Names of the handlers to call. One per line (or comma separated)."
                {...field("handlers")}
              />
            </React.Fragment>
          )}

          {isSocketType(values.type) && (
            <React.Fragment>
              <Divider style={{ margin: "1.5rem 0" }} />
              <Typography variant="subtitle1" gutterBottom>
                Socket
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    label="Host"
                    fullWidth
                    required
                    {...field("socketHost")}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Port"
                    type="number"
                    fullWidth
                    required
                    {...field("socketPort")}
                  />
                </Grid>
              </Grid>
            </React.Fragment>
          )}

          <Divider style={{ margin: "1.5rem 0" }} />
          <Typography variant="subtitle1" gutterBottom>
            Filters and mutator
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Filters"
                fullWidth
                multiline
                helperText="Evaluated before the handler runs. One per line (or comma separated)."
                {...field("filters")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Mutator"
                fullWidth
                helperText="Transforms event data before handling. Leave blank for none."
                {...field("mutator")}
              />
            </Grid>
          </Grid>

          <Divider style={{ margin: "1.5rem 0" }} />
          <Typography variant="subtitle1" gutterBottom>
            Environment and metadata
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Environment variables"
                fullWidth
                multiline
                helperText="One KEY=VALUE per line."
                {...field("envVars")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Runtime assets"
                fullWidth
                multiline
                helperText="One per line (or comma separated)."
                {...field("runtimeAssets")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Labels"
                fullWidth
                multiline
                helperText="One key: value per line."
                {...field("labels")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Annotations"
                fullWidth
                multiline
                helperText="One key: value per line."
                {...field("annotations")}
              />
            </Grid>
          </Grid>

          <Divider style={{ margin: "1.5rem 0" }} />
          <Typography variant="subtitle1" gutterBottom>
            Advanced (raw JSON spec overrides)
          </Typography>
          <TextField
            label="Spec overrides (JSON)"
            fullWidth
            multiline
            rows={4}
            placeholder={'{ "secrets": [{ "name": "TOKEN", "secret": "slack" }] }'}
            helperText="Merged into the handler spec. Use snake_case keys, e.g. secrets, runtime_assets."
            {...field("advanced")}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={submitting}>
            Cancel
          </Button>
          <Button
            autoFocus
            color="primary"
            variant="contained"
            disabled={submitting}
            onClick={submit}
          >
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Loader>
    </Dialog>
  );
};

HandlerConfigForm.propTypes = {
  mode: PropTypes.oneOf(["new", "edit"]).isRequired,
  namespace: PropTypes.string.isRequired,
  initialValues: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

const editHandlerQuery = gql`
  query HandlerConfigDialogQuery($namespace: String!, $name: String!) {
    handler(namespace: $namespace, name: $name) {
      id
      toJSON
    }
  }
`;

const EditHandlerLoader = ({ namespace, name, onClose, onSuccess }) => {
  const query = useQuery({
    query: editHandlerQuery,
    variables: { namespace, name },
    fetchPolicy: "network-only",
  });

  const { data = {}, networkStatus } = query;
  const loading = networkStatus < 6;

  if (loading && !data.handler) {
    return (
      <Dialog open onClose={onClose}>
        <DialogTitle>Edit handler {name}</DialogTitle>
        <DialogContent>
          <Loader loading>
            <div style={{ minHeight: 80 }} />
          </Loader>
        </DialogContent>
      </Dialog>
    );
  }

  if (!data.handler) {
    return null;
  }

  return (
    <HandlerConfigForm
      mode="edit"
      namespace={namespace}
      initialValues={valuesFromResource(data.handler.toJSON)}
      onClose={onClose}
      onSuccess={onSuccess}
    />
  );
};

const HandlerConfigDialog = ({ namespace, handlerName, onClose, onSuccess }) => {
  if (handlerName) {
    return (
      <EditHandlerLoader
        namespace={namespace}
        name={handlerName}
        onClose={onClose}
        onSuccess={onSuccess}
      />
    );
  }

  return (
    <HandlerConfigForm
      mode="new"
      namespace={namespace}
      initialValues={emptyValues(namespace)}
      onClose={onClose}
      onSuccess={onSuccess}
    />
  );
};

HandlerConfigDialog.propTypes = {
  namespace: PropTypes.string.isRequired,
  handlerName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

HandlerConfigDialog.defaultProps = {
  handlerName: null,
  onSuccess: () => null,
};

export default HandlerConfigDialog;
